/**
 * Minimal, dependency-free ZIP reader for WhatsApp chat exports.
 *
 * WHY HAND-ROLLED: a WhatsApp "export with media" zip is routinely 100–700 MB.
 * Mobile Safari gives a tab roughly 1–1.5 GB before it kills it, so the archive
 * must never be held in memory. Every read here goes through Blob.slice(), which
 * the browser services straight off disk — we read the ~kilobytes of central
 * directory, then inflate only the entries we actually want. Peak memory is one
 * photo, not one archive.
 *
 * Inflation uses the platform's own DecompressionStream('deflate-raw') — Safari
 * 16.4+, Chrome 80+, Firefox 113+ — so there is no zip library in the bundle.
 *
 * Deliberately NOT supported (WhatsApp never produces these):
 *   - encrypted entries
 *   - multi-disk archives
 *   - compression methods other than store (0) and deflate (8)
 * Each throws a named error rather than returning wrong bytes.
 */

const SIG_EOCD = 0x06054b50;
const SIG_EOCD64 = 0x06064b50;
const SIG_EOCD64_LOCATOR = 0x07064b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_LOCAL = 0x04034b50;

/** Sentinel meaning "the real value lives in the ZIP64 extra field". */
const U32_MAX = 0xffffffff;
const U16_MAX = 0xffff;

export interface ZipEntry {
  /** Path inside the archive, as stored. */
  name: string;
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
  /** True when the entry is a directory marker rather than a file. */
  isDirectory: boolean;
}

export class ZipError extends Error {
  // Declared as a plain field rather than a constructor parameter property —
  // the project builds with `erasableSyntaxOnly`, which forbids the shorthand.
  readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = "ZipError";
    this.code = code;
  }
}

/** Read [start, end) of a blob as bytes. The only place we touch the file. */
async function readSlice(file: Blob, start: number, end: number): Promise<Uint8Array> {
  const clampedStart = Math.max(0, start);
  const clampedEnd = Math.min(file.size, end);
  if (clampedEnd <= clampedStart) return new Uint8Array(0);
  const buf = await file.slice(clampedStart, clampedEnd).arrayBuffer();
  return new Uint8Array(buf);
}

function u16(v: DataView, off: number): number {
  return v.getUint16(off, true);
}
function u32(v: DataView, off: number): number {
  return v.getUint32(off, true);
}
/** ZIP64 fields are 8 bytes. Values here are always well under 2^53. */
function u64(v: DataView, off: number): number {
  const lo = v.getUint32(off, true);
  const hi = v.getUint32(off + 4, true);
  return hi * 0x100000000 + lo;
}

function decodeName(bytes: Uint8Array, utf8Flag: boolean): string {
  // Modern zips (WhatsApp included) set the UTF-8 flag. When it is absent the
  // spec says CP437, but in practice UTF-8 is still the common case, so decode
  // leniently rather than mangling accented filenames.
  const decoder = new TextDecoder(utf8Flag ? "utf-8" : "utf-8", { fatal: false });
  return decoder.decode(bytes);
}

/**
 * Locate the End Of Central Directory record by scanning backwards.
 * The EOCD is 22 bytes plus a comment of at most 65535, so it always lives in
 * the final ~64 KB.
 */
async function findEocd(file: Blob): Promise<{ view: DataView; offsetInTail: number; tailStart: number }> {
  const maxTail = Math.min(file.size, 22 + 0xffff);
  const tail = await readSlice(file, file.size - maxTail, file.size);
  const view = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);
  for (let i = tail.length - 22; i >= 0; i--) {
    if (u32(view, i) === SIG_EOCD) {
      return { view, offsetInTail: i, tailStart: file.size - maxTail };
    }
  }
  throw new ZipError("Not a zip file (no end-of-archive record found).", "not_a_zip");
}

interface CentralDirLocation {
  offset: number;
  size: number;
  entryCount: number;
}

async function locateCentralDirectory(file: Blob): Promise<CentralDirLocation> {
  const { view, offsetInTail, tailStart } = await findEocd(file);
  let entryCount = u16(view, offsetInTail + 10);
  let size = u32(view, offsetInTail + 12);
  let offset = u32(view, offsetInTail + 16);

  const needsZip64 = entryCount === U16_MAX || size === U32_MAX || offset === U32_MAX;
  if (!needsZip64) return { offset, size, entryCount };

  // ZIP64: the locator sits immediately before the EOCD.
  const locatorAbs = tailStart + offsetInTail - 20;
  const locatorBytes = await readSlice(file, locatorAbs, locatorAbs + 20);
  if (locatorBytes.length < 20) {
    throw new ZipError("Archive looks like ZIP64 but the locator is missing.", "zip64_malformed");
  }
  const locView = new DataView(locatorBytes.buffer, locatorBytes.byteOffset, locatorBytes.byteLength);
  if (u32(locView, 0) !== SIG_EOCD64_LOCATOR) {
    throw new ZipError("Archive looks like ZIP64 but the locator is missing.", "zip64_malformed");
  }
  const eocd64Abs = u64(locView, 8);
  const eocd64Bytes = await readSlice(file, eocd64Abs, eocd64Abs + 56);
  const e64 = new DataView(eocd64Bytes.buffer, eocd64Bytes.byteOffset, eocd64Bytes.byteLength);
  if (u32(e64, 0) !== SIG_EOCD64) {
    throw new ZipError("ZIP64 end-of-archive record is unreadable.", "zip64_malformed");
  }
  entryCount = u64(e64, 32);
  size = u64(e64, 40);
  offset = u64(e64, 48);
  return { offset, size, entryCount };
}

/**
 * Read the archive's index. Costs one small read of the central directory —
 * nothing is inflated and no file content is touched.
 */
export async function readZipIndex(file: Blob): Promise<ZipEntry[]> {
  if (typeof DecompressionStream === "undefined") {
    throw new ZipError(
      "This browser can't unpack zip files. iOS 16.4 or newer is needed.",
      "no_decompression_stream",
    );
  }
  const { offset, size, entryCount } = await locateCentralDirectory(file);
  const cd = await readSlice(file, offset, offset + size);
  const view = new DataView(cd.buffer, cd.byteOffset, cd.byteLength);

  const entries: ZipEntry[] = [];
  let p = 0;
  while (p + 46 <= cd.length && entries.length < entryCount) {
    if (u32(view, p) !== SIG_CENTRAL) break;
    const flags = u16(view, p + 8);
    const method = u16(view, p + 10);
    let compressedSize = u32(view, p + 20);
    let uncompressedSize = u32(view, p + 24);
    const nameLen = u16(view, p + 28);
    const extraLen = u16(view, p + 30);
    const commentLen = u16(view, p + 32);
    let localHeaderOffset = u32(view, p + 42);

    const nameBytes = cd.subarray(p + 46, p + 46 + nameLen);
    const name = decodeName(nameBytes, (flags & 0x800) !== 0);

    // Bit 0 = encrypted. Refuse loudly; never hand back garbage.
    if ((flags & 0x1) !== 0) {
      throw new ZipError(`"${name}" is encrypted — password-protected zips aren't supported.`, "encrypted");
    }

    // ZIP64 extra field carries the real sizes when the 32-bit fields overflow.
    if (uncompressedSize === U32_MAX || compressedSize === U32_MAX || localHeaderOffset === U32_MAX) {
      const extraStart = p + 46 + nameLen;
      let q = extraStart;
      const extraEnd = extraStart + extraLen;
      while (q + 4 <= extraEnd) {
        const headerId = u16(view, q);
        const dataSize = u16(view, q + 2);
        if (headerId === 0x0001) {
          let r = q + 4;
          if (uncompressedSize === U32_MAX) { uncompressedSize = u64(view, r); r += 8; }
          if (compressedSize === U32_MAX) { compressedSize = u64(view, r); r += 8; }
          if (localHeaderOffset === U32_MAX) localHeaderOffset = u64(view, r);
          break;
        }
        q += 4 + dataSize;
      }
    }

    entries.push({
      name,
      method,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
      isDirectory: name.endsWith("/"),
    });
    p += 46 + nameLen + extraLen + commentLen;
  }

  if (entries.length === 0) {
    throw new ZipError("The zip is empty.", "empty_zip");
  }
  return entries;
}

/**
 * Inflate one entry. Reads only that entry's bytes.
 *
 * The local header is re-read because its name/extra lengths can legitimately
 * differ from the central directory's, and the data offset depends on them.
 * Sizes always come from the central directory, which sidesteps the streaming
 * data-descriptor case (flag bit 3) where the local header's sizes are zero.
 */
export async function readZipEntry(file: Blob, entry: ZipEntry): Promise<Uint8Array> {
  if (entry.isDirectory) return new Uint8Array(0);

  const head = await readSlice(file, entry.localHeaderOffset, entry.localHeaderOffset + 30);
  if (head.length < 30) {
    throw new ZipError(`"${entry.name}" is truncated.`, "truncated");
  }
  const hv = new DataView(head.buffer, head.byteOffset, head.byteLength);
  if (u32(hv, 0) !== SIG_LOCAL) {
    throw new ZipError(`"${entry.name}" has a corrupt header.`, "bad_local_header");
  }
  const nameLen = u16(hv, 26);
  const extraLen = u16(hv, 28);
  const dataStart = entry.localHeaderOffset + 30 + nameLen + extraLen;
  const raw = await readSlice(file, dataStart, dataStart + entry.compressedSize);

  if (entry.method === 0) return raw;
  if (entry.method !== 8) {
    throw new ZipError(
      `"${entry.name}" uses an unsupported compression method (${entry.method}).`,
      "unsupported_method",
    );
  }

  const stream = new Blob([raw as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  const out = new Uint8Array(await new Response(stream).arrayBuffer());
  return out;
}

/** Read an entry and decode it as UTF-8 text. */
export async function readZipEntryText(file: Blob, entry: ZipEntry): Promise<string> {
  const bytes = await readZipEntry(file, entry);
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

/** Basename without any directory prefix, for matching chat references. */
export function baseName(path: string): string {
  const i = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return i >= 0 ? path.slice(i + 1) : path;
}

/**
 * The transcript file. WhatsApp names it `_chat.txt` on iOS and
 * `WhatsApp Chat with <name>.txt` on Android; some exports nest it in a folder.
 * Falls back to the largest .txt so a renamed export still works.
 */
export function findChatEntry(entries: ZipEntry[]): ZipEntry | null {
  const files = entries.filter((e) => !e.isDirectory);
  const exact = files.find((e) => baseName(e.name).toLowerCase() === "_chat.txt");
  if (exact) return exact;
  const named = files.find((e) => /^whatsapp chat( with .*)?\.txt$/i.test(baseName(e.name)));
  if (named) return named;
  const txts = files
    .filter((e) => baseName(e.name).toLowerCase().endsWith(".txt"))
    .sort((a, b) => b.uncompressedSize - a.uncompressedSize);
  return txts[0] ?? null;
}
